/**
 * Tankef Design System - Centralized Design Tokens
 *
 * Single source of truth for all design tokens (colors, typography, spacing, etc.)
 * Used by:
 * - Tailwind preset (preset.ts)
 * - @tankef/ui re-exports
 * - CSS custom properties generator
 */

export const tokens = {
  /**
   * Color System
   */
  colors: {
    // Primary - Teal Tankef (wired to CSS vars for dark mode support)
    primary: {
      50: 'var(--color-primary-50)',
      100: 'var(--color-primary-100)',
      200: 'var(--color-primary-200)',
      300: 'var(--color-primary-300)',
      400: 'var(--color-primary-400)',
      500: 'var(--color-primary-500)',
      600: 'var(--color-primary-600)',
      700: 'var(--color-primary-700)',
      800: 'var(--color-primary-800)',
      900: 'var(--color-primary-900)',
      950: 'var(--color-primary-950)',
    },

    // Success - Verde
    success: {
      50: 'var(--color-success-50)',
      100: 'var(--color-success-100)',
      200: 'var(--color-success-200)',
      300: 'var(--color-success-300)',
      400: 'var(--color-success-400)',
      500: 'var(--color-success-500)',
      600: 'var(--color-success-600)',
      700: 'var(--color-success-700)',
      800: 'var(--color-success-800)',
      900: 'var(--color-success-900)',
      950: 'var(--color-success-950)',
    },

    // Warning - Amarillo/Naranja
    warning: {
      50: 'var(--color-warning-50)',
      100: 'var(--color-warning-100)',
      200: 'var(--color-warning-200)',
      300: 'var(--color-warning-300)',
      400: 'var(--color-warning-400)',
      500: 'var(--color-warning-500)',
      600: 'var(--color-warning-600)',
      700: 'var(--color-warning-700)',
      800: 'var(--color-warning-800)',
      900: 'var(--color-warning-900)',
      950: 'var(--color-warning-950)',
    },

    // Error - Rojo Tankef
    error: {
      50: 'var(--color-error-50)',
      100: 'var(--color-error-100)',
      200: 'var(--color-error-200)',
      300: 'var(--color-error-300)',
      400: 'var(--color-error-400)',
      500: 'var(--color-error-500)',
      600: 'var(--color-error-600)',
      700: 'var(--color-error-700)',
      800: 'var(--color-error-800)',
      900: 'var(--color-error-900)',
      950: 'var(--color-error-950)',
    },

    // Gray - Neutral
    gray: {
      50: 'var(--color-gray-50)',
      100: 'var(--color-gray-100)',
      150: 'var(--color-gray-150)',
      200: 'var(--color-gray-200)',
      300: 'var(--color-gray-300)',
      400: 'var(--color-gray-400)',
      500: 'var(--color-gray-500)',
      600: 'var(--color-gray-600)',
      700: 'var(--color-gray-700)',
      800: 'var(--color-gray-800)',
      900: 'var(--color-gray-900)',
      950: 'var(--color-gray-950)',
    },

    // Brand Colors (semantic aliases)
    brand: {
      teal: '#29D6B2',
      tealHover: '#24C4A2',
      dark: '#2C454A',
    },

    // Text Colors (semantic)
    text: {
      primary: '#2C454A',
      secondary: 'rgba(44, 69, 74, 0.6)',
      muted: 'rgba(44, 69, 74, 0.4)',
      inverse: '#FFFFFF',
      disabled: 'rgba(44, 69, 74, 0.3)',
    },

    // Surface Colors (semantic)
    surface: {
      white: '#FFFFFF',
      light: '#fafafa',
      muted: '#F4F5F7',
      dark: '#1C252E',
    },

    // Border Colors (semantic)
    border: {
      default: 'rgba(44, 69, 74, 0.15)',
      light: 'rgba(44, 69, 74, 0.1)',
      medium: 'rgba(44, 69, 74, 0.2)',
      focus: '#29D6B2',
    },

    // Overlay Colors (semantic)
    overlay: {
      dark: 'rgba(0, 0, 0, 0.4)',
      light: 'rgba(255, 255, 255, 0.8)',
      blur: 'rgba(255, 255, 255, 0.9)',
    },

    // Alpha overlays of the brand foreground (#2C454A).
    'text-alpha': {
      5: 'rgba(44, 69, 74, 0.05)',
      10: 'rgba(44, 69, 74, 0.10)',
      15: 'rgba(44, 69, 74, 0.15)',
      20: 'rgba(44, 69, 74, 0.20)',
      40: 'rgba(44, 69, 74, 0.40)',
      50: 'rgba(44, 69, 74, 0.50)',
      52: 'rgba(44, 69, 74, 0.52)',
      60: 'rgba(44, 69, 74, 0.60)',
      80: 'rgba(44, 69, 74, 0.80)',
    },

    // Alpha overlays of the brand primary (#29D6B2).
    'primary-alpha': {
      5: 'rgba(41, 214, 178, 0.05)',
      8: 'rgba(41, 214, 178, 0.08)',
      10: 'rgba(41, 214, 178, 0.10)',
      15: 'rgba(41, 214, 178, 0.15)',
      20: 'rgba(41, 214, 178, 0.20)',
      30: 'rgba(41, 214, 178, 0.30)',
      50: 'rgba(41, 214, 178, 0.50)',
    },

    // ─────────────────────────────────────────────────────────
    // Semantic tokens (tkf-* prefix) — wired to CSS custom properties
    // in packages/ui/src/styles/semantic-vars.css. Dark mode is
    // automatic via the .dark class on <html>.
    //
    // Layer contract:
    //   primitives (primary-500, gray-200)  →  paint values
    //   tkf-*                                →  semantic meaning
    //   utilities (.field-base, .btn-*)      →  composition
    // ─────────────────────────────────────────────────────────
    'tkf-fg': {
      DEFAULT: 'var(--tkf-fg, #2C454A)',
      muted: 'var(--tkf-fg-muted, rgba(44, 69, 74, 0.60))',
      subtle: 'var(--tkf-fg-subtle, rgba(44, 69, 74, 0.4))',
      inverse: 'var(--tkf-fg-inverse, #FFFFFF)',
      disabled: 'var(--tkf-fg-disabled, rgba(44, 69, 74, 0.3))',
      error: 'var(--tkf-fg-error, #E30613)',
      warning: 'var(--tkf-fg-warning, #B45309)',
      success: 'var(--tkf-fg-success, #15803D)',
    },
    'tkf-surface': {
      DEFAULT: 'var(--tkf-surface)',
      page: 'var(--tkf-surface-page)',
      subtle: 'var(--tkf-surface-subtle)',
      muted: 'var(--tkf-surface-muted)',
      dark: 'var(--tkf-surface-dark)',
    },
    'tkf-line': {
      DEFAULT: 'var(--tkf-line, rgba(44, 69, 74, 0.15))',
      light: 'var(--tkf-line-light, rgba(44, 69, 74, 0.1))',
      medium: 'var(--tkf-line-medium, rgba(44, 69, 74, 0.2))',
      focus: 'var(--tkf-line-focus, #29D6B2)',
      error: 'var(--tkf-line-error, #EF5350)',
    },
    'tkf-overlay': {
      dark: 'var(--tkf-overlay-dark)',
      light: 'var(--tkf-overlay-light)',
      blur: 'var(--tkf-overlay-blur)',
    },
  },

  /**
   * Typography
   */
  typography: {
    fontFamily: {
      sans: [
        'Inter',
        'ui-sans-serif',
        'system-ui',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
      ],
      mono: [
        'JetBrains Mono',
        'Fira Code',
        'ui-monospace',
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        'monospace',
      ],
    },

    fontSize: {
      '2xs': ['0.625rem', { lineHeight: '0.75rem' }], // 10px
      xs: ['0.75rem', { lineHeight: '1rem' }], // 12px
      sm: ['0.875rem', { lineHeight: '1.25rem' }], // 14px
      base: ['1rem', { lineHeight: '1.5rem' }], // 16px
      lg: ['1.125rem', { lineHeight: '1.75rem' }], // 18px
      xl: ['1.25rem', { lineHeight: '1.75rem' }], // 20px
      '2xl': ['1.5rem', { lineHeight: '2rem' }], // 24px
      '3xl': ['1.875rem', { lineHeight: '2.25rem' }], // 30px
      '4xl': ['2.25rem', { lineHeight: '2.5rem' }], // 36px
      // Tankef Design System - specific design sizes
      label: ['11.11px', { lineHeight: '130%', letterSpacing: '-0.05em' }],
      datepicker: ['12.49px', { lineHeight: '23px', letterSpacing: '-0.03em' }],
      cell: ['13.33px', { lineHeight: '12px', letterSpacing: '-0.03em' }],
      movement: ['14px', { lineHeight: '130%', letterSpacing: '-0.03em' }],
      'empty-state': ['16px', { lineHeight: '130%', letterSpacing: '-0.05em' }],
      // Escala tipográfica Tankef (ratio 1.2 — minor third)
      h1: ['2.986rem', { lineHeight: '1.1' }], // ~47.78px
      h2: ['2.488rem', { lineHeight: '1.15' }], // ~39.81px
      h3: ['2.074rem', { lineHeight: '1.2' }], // ~33.18px
      h4: ['1.728rem', { lineHeight: '1.25' }], // ~27.65px
      h5: ['1.44rem', { lineHeight: '1.3' }], // ~23.04px
      h6: ['1.2rem', { lineHeight: '1.35' }], // ~19.2px
      small: ['0.833rem', { lineHeight: '1.5' }], // ~13.33px
      micro: ['0.694rem', { lineHeight: '1.5' }], // ~11.11px
    },

    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
    },

    letterSpacing: {
      'tighter-tankef': '-0.05em',
      'tight-tankef': '-0.04em',
      'normal-tankef': '-0.03em',
    },
  },

  /**
   * Spacing System
   * Based on 4px grid
   */
  spacing: {
    none: '0',
    '2xs': '0.125rem', // 2px
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '0.75rem', // 12px
    lg: '1rem', // 16px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '2rem', // 32px
    '4xl': '2.5rem', // 40px
    '5xl': '3rem', // 48px
    '6xl': '4rem', // 64px
    '7xl': '5rem', // 80px
    '8xl': '6rem', // 96px
    // Layout tokens — change here to retheme globally
    card: '0.5rem', // 8px — inner gap between card sections
    section: '1.5rem', // 24px — vertical gap between page-level sections
    '4.5': '1.125rem', // 18px (from preset)
    '5.5': '1.375rem', // 22px (from preset)
    '18': '4.5rem', // from preset
    '88': '22rem', // from preset
    '128': '32rem', // from preset
  },

  /**
   * Border Radius
   */
  borderRadius: {
    none: '0',
    sm: '0.125rem', // 2px
    DEFAULT: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    '2xl': '1rem', // 16px
    '3xl': '1.5rem', // 24px
    '4xl': '2rem', // 32px
    full: '9999px',
  },

  /**
   * Box Shadows
   */
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    soft: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)',
    card: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    'card-hover': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    dropdown: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    'input-focus': '0 0 0 3px rgba(41, 214, 178, 0.15)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
  },

  /**
   * Animations & Transitions
   */
  animation: {
    'fade-in': 'fadeIn 0.2s ease-out',
    'slide-up': 'slideUp 0.2s ease-out',
    'slide-down': 'slideDown 0.2s ease-out',
    'scale-in': 'scaleIn 0.2s ease-out',
    pulse: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  },

  keyframes: {
    fadeIn: {
      '0%': { opacity: '0' },
      '100%': { opacity: '1' },
    },
    slideUp: {
      '0%': { transform: 'translateY(10px)', opacity: '0' },
      '100%': { transform: 'translateY(0)', opacity: '1' },
    },
    slideDown: {
      '0%': { transform: 'translateY(-10px)', opacity: '0' },
      '100%': { transform: 'translateY(0)', opacity: '1' },
    },
    scaleIn: {
      '0%': { transform: 'scale(0.95)', opacity: '0' },
      '100%': { transform: 'scale(1)', opacity: '1' },
    },
    pulse: {
      '0%, 100%': { opacity: '1' },
      '50%': { opacity: '0.5' },
    },
  },

  /**
   * Border Width
   */
  borderWidth: {
    '0.5': '0.5px',
  },

  /**
   * Transitions
   */
  transitionDuration: {
    '400': '400ms',
  },

  /**
   * Z-Index Scale
   */
  zIndex: {
    base: '0',
    dropdown: '1000',
    sticky: '1020',
    fixed: '1030',
    modalBackdrop: '1035',
    modal: '1040',
    popover: '1050',
    tooltip: '1060',
    splash: '9998',
    progress: '9999',
  },

  /**
   * Component-specific sizes
   */
  components: {
    input: {
      sm: '32px',
      md: '40px',
      lg: '48px',
    },
    button: {
      sm: '32px',
      md: '40px',
      lg: '48px',
    },
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      // Anchos semánticos de flujo (max-w-narrow|flow|wide via Tailwind utilities).
      // Ver docs/plans/release/1.0.0/03-container-width-tokens.md
      narrow: '560px',
      flow: '850px',
      wide: '1200px',
    },
    sidebar: {
      collapsed: '80px',
      expanded: '280px',
    },
    header: '64px',
  },
} as const;

/**
 * Dark mode is handled automatically via CSS custom properties in
 * packages/ui/src/styles/semantic-vars.css under the .dark scope.
 * No separate token export is needed — toggling the .dark class on
 * <html> switches every tkf-* token atomically.
 */

/**
 * Type definitions for tokens
 */
export type Tokens = typeof tokens;
export type ColorTokens = typeof tokens.colors;
export type TypographyTokens = typeof tokens.typography;
export type SpacingTokens = typeof tokens.spacing;
export type ShadowTokens = typeof tokens.shadows;
export type BorderRadiusTokens = typeof tokens.borderRadius;
export type AnimationTokens = typeof tokens.animation;
export type ZIndexTokens = typeof tokens.zIndex;
export type ComponentTokens = typeof tokens.components;

export default tokens;
